import React from 'react';
import { Edit, Trash } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Category } from '../../types';
import { useInventory } from '../../context/InventoryContext';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';

interface CategoryCardProps {
  category: Category;
  onDelete: (id: string) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, onDelete }) => {
  const { products, getCategoryName, isLowStock } = useInventory();
  const productCount = products.filter(p => p.category_id === category.id).length;

  return (
    <Card className="h-full flex flex-col transition-all duration-200 hover:shadow-md">
      <div 
        className="h-2 rounded-t-lg" 
        style={{ backgroundColor: category.color }}
      ></div>
      <CardContent className="flex-1 flex flex-col p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{category.name}</h3>
        <p className="text-sm text-gray-500 flex-grow mb-4">{category.description}</p>
        <div className="mt-auto">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-medium text-gray-700">Products:</span>
            <span className="text-lg font-bold text-blue-900">{productCount}</span>
          </div>
          <div className="flex justify-between gap-2">
            <Link to={`/categories/${category.id}`} className="flex-1">
              <Button variant="secondary" fullWidth>
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </Link>
            <div className="relative group">
              <Button
                variant="danger"
                onClick={() => onDelete(category.id)}
                disabled={productCount > 0}
                title={productCount > 0 ? "Cannot delete category with products" : "Delete category"}
              >
                <Trash className="h-4 w-4" />
              </Button>
              {productCount > 0 && (
                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-gray-900 text-white text-xs rounded py-2 px-3 z-10">
                  Cannot delete category with {productCount} product{productCount !== 1 ? 's' : ''}. Remove products first.
                  <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoryCard;